// Order Consolidation: group active orders of the same BOM item (and identical stage recipe) into one batch row.
const ACTIVE = new Set(["Pending", "In Progress", "Delayed"]);
const PRIORITY_RANK = { High: 0, Medium: 1, Low: 2 };
const f = (v) => Number(v) || 0;

const recipeKey = (o) => (o.stages || []).map((s) => `${s.name}|${s.type || "in_house"}`).join(">");
const today = () => new Date().toISOString().slice(0, 10);

export const isOverdue = (o) => !!o.due_date && o.status !== "Completed" && o.due_date.slice(0, 10) < today();
export const isUrgent = (o) => o.priority === "High" || o.status === "Delayed" || isOverdue(o);

// Synthetic order whose stages sum the members' quantities, so stageViews()/orderFlow() work unchanged.
function synthesize(members, key) {
  const first = members[0];
  const stages = (first.stages || []).map((st, i) => ({
    name: st.name,
    type: st.type,
    qty_done: members.reduce((a, o) => a + f(o.stages?.[i]?.qty_done), 0),
    qty_shipped: members.reduce((a, o) => a + f(o.stages?.[i]?.qty_shipped), 0),
    gate_pass_ids: members.flatMap((o) => o.stages?.[i]?.gate_pass_ids || []),
  }));
  const sorted = [...members].sort((a, b) => (a.due_date || "").localeCompare(b.due_date || ""));
  const statuses = new Set(members.map((o) => o.status));
  const status = statuses.has("Delayed") ? "Delayed" : statuses.has("In Progress") ? "In Progress" : "Pending";
  return {
    id: `batch:${key}`,
    isBatch: true,
    order_code: `BATCH · ${members.length}`,
    product_name: first.product_name,
    bom_item_id: first.bom_item_id,
    target_quantity: members.reduce((a, o) => a + f(o.target_quantity), 0),
    produced_quantity: members.reduce((a, o) => a + f(o.produced_quantity), 0),
    due_date: sorted[0]?.due_date,
    priority: members.reduce((p, o) => (PRIORITY_RANK[o.priority] < PRIORITY_RANK[p] ? o.priority : p), "Low"),
    status,
    stages,
    stage_history: members.flatMap((o) => o.stage_history || []),
    line_ids: [...new Set(members.map((o) => o.line_id).filter(Boolean))],
    line_names: [...new Set(members.map((o) => o.line_name).filter(Boolean))],
    has_conflict: members.some((o) => o.has_conflict),
    members: sorted,
    urgentCount: members.filter(isUrgent).length,
  };
}

// Returns rows: batches (2+ active orders sharing bom_item_id + recipe) and untouched singles.
export function consolidateOrders(orders) {
  const groups = new Map();
  const singles = [];
  for (const o of orders) {
    if (!ACTIVE.has(o.status) || !o.bom_item_id || !(o.stages || []).length) { singles.push(o); continue; }
    const key = `${o.bom_item_id}::${recipeKey(o)}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(o);
  }
  const rows = [...singles];
  for (const [key, members] of groups) {
    if (members.length > 1) rows.push(synthesize(members, key));
    else rows.push(members[0]);
  }
  return rows;
}

// Suggested split of `qty` across members for stage `idx`, capped by each member's available quantity.
export function suggestSplit(batch, idx, qty, mode, availableOf) {
  const members = mode === "fifo" ? batch.members : [...batch.members].sort((a, b) => (isUrgent(b) ? 1 : 0) - (isUrgent(a) ? 1 : 0));
  const avail = Object.fromEntries(members.map((o) => [o.id, availableOf(o, idx)]));
  const out = Object.fromEntries(members.map((o) => [o.id, 0]));
  if (mode === "proportional") {
    const total = members.reduce((a, o) => a + avail[o.id], 0);
    let left = qty;
    members.forEach((o, i) => {
      const share = i === members.length - 1 ? left : Math.min(avail[o.id], Math.floor((qty * avail[o.id]) / (total || 1)));
      out[o.id] = Math.min(avail[o.id], share);
      left -= out[o.id];
    });
    return out;
  }
  let left = qty;
  for (const o of members) {
    const take = Math.min(avail[o.id], left);
    out[o.id] = take;
    left -= take;
    if (left <= 0) break;
  }
  return out;
}
